import React, { useState, useEffect } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Grid, Button, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PropTypes from "prop-types";
import SSOApiService from "../../../services/SsoServices";
import { CONTENT_PREVIEW_FILE } from "../../../constants/api-constants";
import DocumentViewer from "../../../common/DocumentViewer/DocumentViewer";
import XFDLViewer from "../../../common/XFDLViewer/XFDLViewer";
import { AudioPreview } from "../../../common/AudioPreview/AudioPreview";
import { VideoPreview } from "../../../common/VideoPreview/VideoPreview";
import "./purchase.css";

/*istanbul ignore next */
const ContentPreviewPage = () => {
  const { contentId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contentData, setContentData] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState([]);
  const [fileContent, setFileContent] = useState(null);
  const [previewType, setPreviewType] = useState(0);
  const [marketingDescription, setMarketingDescription] = useState("");
  const [isPreviewAllowed, setIsPreviewAllowed] = useState(true);

  // Type of content mapping for file types
  const typeOfContentOptions = [
    { value: 1, fileType: "pdf", accept: ".pdf" },
    { value: 2, fileType: "xfdl", accept: ".xfdl" },
    { value: 3, fileType: "jpg", accept: ".jpg,.jpeg" },
    { value: 4, fileType: "docx", accept: ".doc,.docx" },
    { value: 5, fileType: "xlsx", accept: ".xls,.xlsx" },
    { value: 6, fileType: "pptx", accept: ".ppt,.pptx" },
    { value: 7, fileType: "audio", accept: ".mp3,.wav,.ogg" },
    { value: 8, fileType: "gif", accept: ".gif" },
    { value: 9, fileType: "png", accept: ".png" },
    { value: 10, fileType: "video", accept: ".mp4,.webm,.ogg" },
    { value: 11, fileType: "link", accept: "" },
  ];

  // Content types that use DocumentViewer (PDF, Office docs, images)
  const allowedContentTypes = new Set([1, 3, 4, 5, 6, 8, 9]);

  useEffect(() => {
    if (contentId) {
      loadContentPreview();
    }
  }, [contentId]);

  const loadContentPreview = async () => {
    try {
      setLoading(true);
      setError(null);
      const stateData = location.state || {};
      const isPreviewFromState = stateData.isPreview;
      const previewTypeFromState = stateData.previewType;
      const marketingDescriptionFromState = stateData.marketingDescription;

      const response = await SSOApiService.ssoGet(
        CONTENT_PREVIEW_FILE.replace("{contentId}", contentId),
      );

      if (response?.responseStatus?.isSuccess && response?.result) {
        const result = response.result;

        setContentData(result);

        const finalIsPreview =
          result.isPreview !== undefined
            ? result.isPreview
            : isPreviewFromState;
        const finalPreviewType =
          result.previewType !== undefined
            ? result.previewType
            : previewTypeFromState;
        const finalMarketingDescription =
          result.marketingDescription || marketingDescriptionFromState || "";

        // Set preview state
        setIsPreviewAllowed(finalIsPreview === true);
        setPreviewType(finalPreviewType || 0);
        setMarketingDescription(finalMarketingDescription);

        // Only load content if preview is allowed
        if (finalIsPreview === true) {
          handleContentTypeDisplay(result);
        }
      } else {
        setError(
          response?.responseStatus?.description ||
            "Failed to load content preview",
        );
      }
    } catch (err) {
      console.error("Error loading content preview:", err);
      setError(
        "An error occurred while loading the preview. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleXfdlFromJson = (xfdlJson) => {
    try {
      setFileContent(JSON.parse(xfdlJson));
    } catch (e) {
      console.error("Error parsing XFDL JSON:", e);
      setError("Failed to parse XFDL content");
    }
  };

  const handleXfdlFromFile = async (filePath, fileName) => {
    try {
      const xfdlResponse = await fetch(filePath);
      const xfdlText = await xfdlResponse.text();
      try {
        const parsedXfdl = JSON.parse(xfdlText);
        setFileContent(parsedXfdl);
      } catch (parseError) {
        console.error("XFDL file could not be parsed:", parseError);
        setSelectedDocument([
          {
            uri: filePath,
            fileName: fileName || "Preview",
            fileType: "xfdl",
          },
        ]);
      }
    } catch (fetchError) {
      console.error("Error fetching XFDL file:", fetchError);
      setError("Failed to load XFDL content");
    }
  };

  const handleContentTypeDisplay = async (result) => {
    const contentType = determineContentType(result);

    if (contentType === 11 && result.webLink) {
      window.open(result.webLink, "_blank");
      return;
    }

    if (contentType === 2) {
      if (result.xfdlJson) {
        handleXfdlFromJson(result.xfdlJson);
      } else if (result.filePath) {
        await handleXfdlFromFile(result.filePath, result.fileName);
      } else {
        setError("No XFDL content available for preview");
      }
      return;
    }

    if (result.filePath) {
      const fileType = typeOfContentOptions.find(
        (item) => item.value === contentType,
      )?.fileType;
      setSelectedDocument([
        {
          uri: result.filePath,
          fileName: result.fileName || "Preview",
          fileType: fileType || "pdf",
        },
      ]);
      return;
    }

    setError("No content available for preview");
  };

  const determineContentType = (result) => {
    if (result.xfdlJson) return 2;
    if (result.webLink) return 11;
    if (result.fileName) {
      const ext = result.fileName.toLowerCase().split(".").pop();
      const typeMap = {
        pdf: 1,
        xfdl: 2,
        jpg: 3,
        jpeg: 3,
        doc: 4,
        docx: 4,
        xls: 5,
        xlsx: 5,
        ppt: 6,
        pptx: 6,
        mp3: 7,
        wav: 7,
        ogg: 7,
        gif: 8,
        png: 9,
        mp4: 10,
        webm: 10,
      };
      return typeMap[ext] || 1; // Default to PDF
    }

    return 1; // Default to PDF
  };

  const renderPreview = () => {
    if (!contentData) return null;
    if (!isPreviewAllowed) {
      return (
        <div className="no-preview">
          <img
            src="/images/no-preview-available.png"
            alt="No preview available"
          />
        </div>
      );
    }

    if (previewType === 3) {
      return (
        <div style={{ padding: "20px", color: "#333" }}>
          <h3>Marketing Description</h3>
          {marketingDescription ? (
            <p style={{ whiteSpace: "pre-wrap" }}>{marketingDescription}</p>
          ) : (
            <p>No marketing description available.</p>
          )}
        </div>
      );
    }
    const contentType = determineContentType(contentData);
    // Audio content
    if (contentType === 7) {
      return <AudioPreview selectedDocument={selectedDocument} />;
    }

    // Video content
    if (contentType === 10) {
      return <VideoPreview selectedDocument={selectedDocument} />;
    }

    // XFDL content
    if (contentType === 2) {
      if (fileContent) {
        return (
          <XFDLViewer
            document={fileContent}
            fileName={contentData.fileName || "Preview"}
            from="preview"
            previewType={previewType}
            showPageNumbers={true}
          />
        );
      } else if (selectedDocument.length > 0) {
        // If XFDL file couldn't be parsed, show download option
        return (
          <div className="xfdl-download-container">
            <h3>XFDL Form Preview</h3>
            <p>
              This XFDL form cannot be previewed in the browser. Please download
              the file to view it.
            </p>
            <Button
              variant="contained"
              href={selectedDocument[0].uri}
              download={selectedDocument[0].fileName}
              className="action-button"
            >
              Download XFDL File
            </Button>
          </div>
        );
      }
    }

    // Document viewer for PDFs, Office docs, images
    if (allowedContentTypes.has(contentType) && selectedDocument.length > 0) {
      return (
        <DocumentViewer
          disableHeader={true}
          tempId={Math.floor(Math.random() * 1000 + 1)}
          documents={selectedDocument}
          previewType={previewType}
          config={{
            noRenderer: {
              overrideComponent: <div>No Document available for preview</div>,
            },
          }}
        />
      );
    }

    // No preview available
    return (
      <div className="no-preview">
        <img
          src="/images/no-preview-available.png"
          alt="No preview available"
        />
      </div>
    );
  };

  const handleBackClick = () => {
    history.goBack();
  };

  if (loading) {
    return (
      <div className="preview-loading-container">
        <div
          className="sso-loader-wrapper"
          style={{ padding: "20px", color: "white" }}
        >
          <div className="sso-loader" />
          <p>Loading preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="preview-error-container">
        <h3>Error Loading Preview</h3>
        <p>{error}</p>
        <Button
          variant="contained"
          className="action-button"
          onClick={handleBackClick}
        >
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="content-preview-page">
      <Grid container className="preview-header">
        <Grid item xs={12}>
          <div className="preview-header-content">
            <IconButton
              className="action-button"
              onClick={handleBackClick}
              aria-label="Go back"
              style={{
                marginLeft: "-20px",
                marginRight: "-18px",
                color: "#fff",
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <h2>{contentData?.fileName || "Content Preview"}</h2>
          </div>
        </Grid>
      </Grid>

      <Grid container className="preview-body">
        <Grid item xs={12}>
          <div className="preview-content-wrapper">{renderPreview()}</div>
        </Grid>
      </Grid>
    </div>
  );
};

ContentPreviewPage.propTypes = {
  contentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ContentPreviewPage;
